import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Papa from "papaparse";
import Select from "react-select";
import { Grid, Card, Form, Button, Icon, Alert } from "tabler-react";
import { NotificationManager } from "react-notifications";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import { LoadingScreen } from "../../components/LoadingScreen";
import SiteWrapper from "../../SiteWrapper.react";
import styles from "./AsignacionMasivaGps.module.css";

const MAX_REGISTROS = 1000;
// Se envian lotes pequenos para mostrar el avance real (el backend acepta hasta 500)
const TAMANO_LOTE = 50;

// GPS virtuales (catalogos.gps con id <= 20): no reportan en GpsGate
const GPS_VIRTUALES = [
  { value: 1, label: "CUENTA ESPEJO 2" },
  { value: 2, label: "CUENTA ESPEJO" },
  { value: 3, label: "NO CUMPLIO" },
  { value: 4, label: "RADIO OPERADO" },
  { value: 5, label: "SIN COBERTURA" },
  { value: 6, label: "PENDIENTE SIN ASIGNACION DE LT" },
  { value: 7, label: "PENDIENTE" },
  { value: 8, label: "SIN EQUIPO SKY" },
  { value: 9, label: "SIN VARIABLE" },
  { value: 10, label: "VALIDACION" },
  { value: 11, label: "Causa Natural" },
  { value: 12, label: "REPROGRAMADO" },
  { value: 13, label: "SIN ASIGNACIÓN" },
  { value: 14, label: "SALIDA EN FALSO" },
  { value: 15, label: "BITACORA TARDIA" },
].map((g) => ({ ...g, label: g.value + " · " + g.label }));

/**
 * AsignacionMasivaGps (Monitoreo / Navieras)
 * -----------------------------------------------------------------------------
 * Flujo guiado en 4 pasos para asignar un mismo GPS virtual a varios movimientos
 * (relacion monitoreo.movimiento_gps, tipo de colocacion 1) a partir de un CSV con IDs.
 * Opcionalmente registra una misma incidencia en cada movimiento al que se le asignó
 * el GPS (solo si la incidencia está habilitada para la plataforma del movimiento).
 *
 * Endpoint backend:
 *   POST /movimientoGps/masivo  { idGps, idsMovimiento: [], idIncidencia?, comentarioIncidencia? }
 */
const AsignacionMasivaGps = () => {
  /* ----------------------------- Estado --------------------------------- */
  const [step, setStep] = useState(1);

  // Paso 1
  const [gps, setGps] = useState(null); // { value, label }
  const [agregarIncidencia, setAgregarIncidencia] = useState(false);
  const [catalogoIncidencias, setCatalogoIncidencias] = useState([]);
  const [cargandoIncidencias, setCargandoIncidencias] = useState(false);
  const [incidencia, setIncidencia] = useState(null); // { value, label }
  const [comentarioIncidencia, setComentarioIncidencia] = useState("");

  // Paso 2
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [ids, setIds] = useState([]); // IDs válidos únicos (números)
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(5);
  const [duplicados, setDuplicados] = useState(0);
  const [invalidas, setInvalidas] = useState([]); // líneas inválidas [{linea, valor}]
  const [csvError, setCsvError] = useState("");

  // Paso 3
  const [confirmado, setConfirmado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progreso, setProgreso] = useState({ procesados: 0, total: 0 });

  // Paso 4
  const [resultados, setResultados] = useState([]);

  const fileInputRef = useRef(null);

  /* --------------------- Catálogo de incidencias ------------------------ */
  // Se carga solo la primera vez que se activa "Agregar incidencia"
  useEffect(() => {
    if (!agregarIncidencia || catalogoIncidencias.length > 0) return;
    setCargandoIncidencias(true);
    Crud_Catalogos("incidencias", "incidencias", "get", "", [], "", "", "", [])
      .then((data) => {
        if (Array.isArray(data)) {
          const opts = data
            .map((inc) => ({ value: inc.id, label: inc.nombre }))
            .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
          setCatalogoIncidencias(opts);
        }
      })
      .catch(() =>
        NotificationManager.error("Error al cargar el catálogo de incidencias", "Error")
      )
      .finally(() => setCargandoIncidencias(false));
  }, [agregarIncidencia, catalogoIncidencias.length]);

  /* ----------------------------- Derivados ------------------------------ */
  const totalPreviewPages = Math.max(1, Math.ceil(ids.length / previewPageSize));
  const preview = useMemo(() => {
    const inicio = (previewPage - 1) * previewPageSize;
    return ids.slice(inicio, inicio + previewPageSize);
  }, [ids, previewPage, previewPageSize]);
  const previewInicioIdx = (previewPage - 1) * previewPageSize;

  const puedeAvanzarPaso1 = !!gps && (!agregarIncidencia || !!incidencia);
  const puedeAvanzarPaso2 = ids.length > 0 && !csvError;
  const puedeConfirmar = confirmado && puedeAvanzarPaso2 && !loading;

  const porcentaje = progreso.total
    ? Math.round((progreso.procesados / progreso.total) * 100)
    : 0;

  const grupos = useMemo(
    () => ({
      asignados: resultados.filter((r) => r.estatus === "ASIGNADO"),
      conGps: resultados.filter((r) => r.motivo === "YA_TIENE_GPS"),
      noColocables: resultados.filter(
        (r) => r.motivo === "TERMINADO" || r.motivo === "ELIMINADO"
      ),
      errores: resultados.filter((r) => r.estatus === "ERROR"),
      incidenciasRegistradas: resultados.filter((r) => r.incidencia === "REGISTRADA"),
      incidenciasNoRegistradas: resultados.filter(
        (r) => r.incidencia === "NO_HABILITADA" || r.incidencia === "ERROR"
      ),
    }),
    [resultados]
  );
  // Si la carga incluyó incidencia (se conserva para mostrarla en resultados)
  const conIncidencia = agregarIncidencia && !!incidencia;

  /* --------------------------- Parseo de CSV ---------------------------- */
  const procesarArchivo = useCallback((file) => {
    setCsvError("");
    setIds([]);
    setPreviewPage(1);
    setPreviewPageSize(5);
    setDuplicados(0);
    setInvalidas([]);

    if (!file) return;

    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (extension !== "csv") {
      setCsvError("El archivo no es válido. Debe ser un .csv");
      NotificationManager.warning("Selecciona un archivo .csv", "Archivo inválido");
      return;
    }

    setFileName(file.name);
    setParsing(true);

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      try {
        const resultado = Papa.parse(target.result, {
          header: true,
          skipEmptyLines: true,
        });
        const filas = resultado?.data || [];

        if (filas.length === 0) {
          setCsvError("El archivo está vacío.");
          setParsing(false);
          return;
        }

        // Detectar la columna de id (id, ID, id_movimiento, etc.)
        const columnas = Object.keys(filas[0] || {});
        const columnaId =
          columnas.find((c) => c.trim().toLowerCase() === "id") ||
          columnas.find((c) =>
            c.trim().toLowerCase().replace(/[\s_]/g, "").includes("idmovimiento")
          ) ||
          columnas[0];

        if (filas.length > MAX_REGISTROS) {
          setCsvError(
            `El archivo excede el máximo de ${MAX_REGISTROS.toLocaleString()} registros (tiene ${filas.length.toLocaleString()}).`
          );
          setParsing(false);
          return;
        }

        const vistos = new Set();
        const validos = [];
        const invalidasTmp = [];
        let dupCount = 0;

        filas.forEach((fila, index) => {
          const raw = (fila[columnaId] ?? "").toString().trim();
          if (raw === "") {
            invalidasTmp.push({ linea: index + 2, valor: "(vacío)" });
            return;
          }
          if (!/^\d+$/.test(raw)) {
            invalidasTmp.push({ linea: index + 2, valor: raw });
            return;
          }
          const num = Number(raw);
          if (vistos.has(num)) {
            dupCount += 1;
            return;
          }
          vistos.add(num);
          validos.push(num);
        });

        setIds(validos);
        setDuplicados(dupCount);
        setInvalidas(invalidasTmp);

        if (validos.length === 0) {
          setCsvError("No se detectó ningún ID válido en el archivo.");
        }
      } catch (e) {
        setCsvError("No se pudo leer el archivo CSV.");
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setCsvError("Ocurrió un error al leer el archivo.");
      setParsing(false);
    };
    reader.readAsText(file);
  }, []);

  /* ----------------------------- Drag & Drop ---------------------------- */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    procesarArchivo(file);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleFileInput = (e) => {
    const file = e.target.files && e.target.files[0];
    procesarArchivo(file);
  };
  const abrirSelector = () => fileInputRef.current && fileInputRef.current.click();
  const onZoneKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      abrirSelector();
    }
  };

  const limpiarArchivo = () => {
    setFileName("");
    setIds([]);
    setPreviewPage(1);
    setDuplicados(0);
    setInvalidas([]);
    setCsvError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* --------------------------- Descargas CSV ---------------------------- */
  const descargarCsv = (contenido, nombre) => {
    // BOM para que Excel respete UTF-8 y no rompa acentos.
    const blob = new Blob(["﻿" + contenido], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", nombre);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const descargarPlantilla = () => {
    const contenido =
      "id\n" + ["1001", "1002", "1003", "1004", "1005"].join("\n") + "\n";
    descargarCsv(contenido, "plantilla_asignacion_masiva_gps.csv");
  };

  const descargarResultados = () => {
    const escapar = (v) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
    const filas = resultados.map((r) =>
      [r.idMovimiento, r.estatus, r.motivo, r.gpsActual, r.mensaje, r.incidencia, r.incidenciaMensaje]
        .map(escapar)
        .join(",")
    );
    const contenido =
      "id_movimiento,estatus,motivo,gps_actual,mensaje,incidencia,incidencia_mensaje\n" +
      filas.join("\n") +
      "\n";
    descargarCsv(contenido, "resultado_asignacion_masiva_gps.csv");
  };

  /* ---------------------------- Asignación ------------------------------ */
  const marcarLote = (lote, mensaje) =>
    lote.map((idMovimiento) => ({
      idMovimiento,
      estatus: "ERROR",
      motivo: "ERROR",
      mensaje,
    }));

  const ejecutarAsignacion = async () => {
    if (!puedeConfirmar) return;
    setLoading(true);
    setProgreso({ procesados: 0, total: ids.length });

    const datosIncidencia = conIncidencia
      ? { idIncidencia: incidencia.value, comentarioIncidencia: comentarioIncidencia.trim() }
      : {};

    const acumulados = [];
    for (let i = 0; i < ids.length; i += TAMANO_LOTE) {
      const lote = ids.slice(i, i + TAMANO_LOTE);
      try {
        const res = await Crud_Catalogos(
          "movimientoGps/masivo",
          "movimientoGps",
          "post",
          "",
          { idGps: gps.value, idsMovimiento: lote, ...datosIncidencia }
        );
        if (res && Array.isArray(res.resultados)) {
          acumulados.push(...res.resultados);
        } else {
          acumulados.push(
            ...marcarLote(lote, (res && res.Error) || "Respuesta inesperada del servidor")
          );
        }
      } catch (e) {
        acumulados.push(...marcarLote(lote, "Falló la petición de este lote"));
      }
      setProgreso({ procesados: Math.min(i + lote.length, ids.length), total: ids.length });
    }

    const asignados = acumulados.filter((r) => r.estatus === "ASIGNADO").length;
    const omitidos = acumulados.filter((r) => r.estatus === "OMITIDO").length;
    if (asignados > 0) {
      NotificationManager.success(
        asignados + " movimiento(s) con GPS asignado",
        "Asignación completada"
      );
    }
    if (omitidos > 0) {
      NotificationManager.warning(
        omitidos + " movimiento(s) omitido(s), revisa el detalle",
        "Aviso"
      );
    }

    setResultados(acumulados);
    setLoading(false);
    setStep(4);
  };

  const reiniciar = () => {
    setStep(1);
    setGps(null);
    setAgregarIncidencia(false);
    setIncidencia(null);
    setComentarioIncidencia("");
    setConfirmado(false);
    setResultados([]);
    setProgreso({ procesados: 0, total: 0 });
    limpiarArchivo();
  };

  /* ------------------------- Indicador de pasos ------------------------- */
  const Stepper = () => {
    const pasos = [
      { n: 1, label: "GPS" },
      { n: 2, label: "Cargar CSV" },
      { n: 3, label: "Confirmar" },
      { n: 4, label: "Resultados" },
    ];
    return (
      <div className={styles.stepper} role="list" aria-label="Progreso">
        {pasos.map((p, i) => (
          <React.Fragment key={p.n}>
            <div
              role="listitem"
              aria-current={step === p.n ? "step" : undefined}
              className={
                styles.step +
                " " +
                (step === p.n ? styles.stepActive : step > p.n ? styles.stepDone : "")
              }
            >
              <span className={styles.stepCircle}>
                {step > p.n ? <Icon name="check" /> : p.n}
              </span>
              <span className={styles.stepLabel}>{p.label}</span>
            </div>
            {i < pasos.length - 1 && (
              <span
                className={
                  styles.stepConnector + " " + (step > p.n ? styles.stepConnectorDone : "")
                }
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  /* ------------------------ Tabla de resultados ------------------------- */
  const TablaResultados = ({ titulo, icono, variante, filas, columnaDetalle, detalle }) => {
    if (filas.length === 0) return null;
    return (
      <div className={styles.resultSection}>
        <div className={styles.resultTitle + " " + styles[variante]}>
          <Icon name={icono} className="mr-2" />
          {titulo} ({filas.length.toLocaleString()})
        </div>
        <div className={styles.previewScroll}>
          <table className={styles.previewTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>ID de movimiento</th>
                <th>{columnaDetalle}</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((r, i) => (
                <tr key={r.idMovimiento}>
                  <td>{i + 1}</td>
                  <td>{r.idMovimiento}</td>
                  <td>{detalle(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /* -------------------------------- Render ------------------------------ */
  return (
    <SiteWrapper>
      <div className="container my-4">
        <Grid.Row>
          <Grid.Col lg={8} md={10} sm={12} className="mx-auto">
            <Card>
              <Card.Header>
                <Card.Title>
                  <Icon name="map-pin" className="mr-2 text-muted" />
                  Asignación Masiva de GPS
                </Card.Title>
              </Card.Header>

              <Card.Body>
                <Stepper />

                {/* =============================== PASO 1 =============================== */}
                {step === 1 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Paso 1 · Seleccionar GPS a colocar</h4>
                    <Form.Group
                      label={
                        <span>
                          GPS <span className="text-danger">*</span>
                        </span>
                      }
                    >
                      <label htmlFor="gps-select" className="sr-only">
                        GPS a colocar
                      </label>
                      <Select
                        inputId="gps-select"
                        aria-label="GPS a colocar"
                        placeholder="Selecciona un GPS..."
                        options={GPS_VIRTUALES}
                        value={gps}
                        onChange={(opt) => setGps(opt)}
                        noOptionsMessage={() => "Sin GPS disponibles"}
                      />
                      <small className={styles.help}>
                        Este GPS se asignará a todos los movimientos del archivo.
                      </small>
                    </Form.Group>

                    <label className={styles.confirmCheck} htmlFor="agregarIncidencia">
                      <input
                        id="agregarIncidencia"
                        type="checkbox"
                        checked={agregarIncidencia}
                        onChange={(e) => {
                          setAgregarIncidencia(e.target.checked);
                          if (!e.target.checked) {
                            setIncidencia(null);
                            setComentarioIncidencia("");
                          }
                        }}
                      />
                      <span>Agregar una incidencia a cada movimiento</span>
                    </label>

                    {agregarIncidencia && (
                      <div className={styles.incidenciaBox}>
                        <Form.Group
                          label={
                            <span>
                              Incidencia <span className="text-danger">*</span>
                            </span>
                          }
                        >
                          <label htmlFor="incidencia-select" className="sr-only">
                            Incidencia
                          </label>
                          <Select
                            inputId="incidencia-select"
                            aria-label="Incidencia"
                            placeholder={
                              cargandoIncidencias
                                ? "Cargando incidencias..."
                                : "Busca y selecciona una incidencia..."
                            }
                            isLoading={cargandoIncidencias}
                            options={catalogoIncidencias}
                            value={incidencia}
                            onChange={(opt) => setIncidencia(opt)}
                            noOptionsMessage={() => "Sin incidencias"}
                          />
                        </Form.Group>
                        <Form.Group label="Comentario">
                          <textarea
                            id="comentarioIncidencia"
                            className="form-control"
                            rows={3}
                            maxLength={500}
                            placeholder="Comentario que se guardará en cada incidencia (opcional)"
                            value={comentarioIncidencia}
                            onChange={(e) => setComentarioIncidencia(e.target.value)}
                          />
                        </Form.Group>
                        <small className={styles.help}>
                          Se registrará solo en los movimientos a los que se les asigne el GPS y
                          donde la incidencia esté habilitada para su plataforma. No se envían
                          notificaciones.
                        </small>
                      </div>
                    )}

                    <div className={styles.actions}>
                      <Button
                        color="primary"
                        disabled={!puedeAvanzarPaso1}
                        onClick={() => setStep(2)}
                      >
                        Continuar <Icon name="arrow-right" className="ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* =============================== PASO 2 =============================== */}
                {step === 2 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Paso 2 · Cargar archivo CSV</h4>

                    <div className={styles.templateBox}>
                      <div className={styles.templateInfo}>
                        <Icon name="info" className={styles.templateIcon} />
                        <div>
                          <strong>¿No sabes cómo debe ser el archivo?</strong>
                          <div className={styles.help}>
                            El CSV debe tener una columna llamada <code>id</code> y un ID de
                            movimiento por línea. Descarga la plantilla de ejemplo:
                          </div>
                        </div>
                      </div>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={descargarPlantilla}
                        aria-label="Descargar plantilla CSV de ejemplo"
                      >
                        <Icon name="download" className="mr-1" /> Descargar plantilla
                      </Button>
                    </div>

                    <div
                      className={
                        styles.dropzone + " " + (isDragging ? styles.dropzoneActive : "")
                      }
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={abrirSelector}
                      onKeyDown={onZoneKeyDown}
                      role="button"
                      tabIndex={0}
                      aria-label="Zona para arrastrar y soltar el archivo CSV, o presiona Enter para seleccionarlo"
                    >
                      <Icon name="upload-cloud" className={styles.dropIcon} />
                      <div className={styles.dropText}>
                        <strong>Arrastra y suelta</strong> tu archivo aquí
                        <br />
                        o <span className={styles.link}>haz clic para seleccionarlo</span>
                      </div>
                      <small className={styles.help}>
                        Formato: columna <code>id</code> (un ID por línea) · Máx{" "}
                        {MAX_REGISTROS.toLocaleString()} registros
                      </small>
                      <input
                        ref={fileInputRef}
                        id="csvInput"
                        name="file"
                        type="file"
                        accept=".csv"
                        onChange={handleFileInput}
                        className={styles.hiddenInput}
                      />
                    </div>

                    {fileName && (
                      <div className={styles.fileChip}>
                        <Icon name="file-text" className="mr-2" />
                        <span className={styles.fileName}>{fileName}</span>
                        <button
                          type="button"
                          className={styles.chipRemove}
                          onClick={limpiarArchivo}
                          aria-label="Quitar archivo"
                        >
                          <Icon name="x" />
                        </button>
                      </div>
                    )}

                    {parsing && (
                      <div className={styles.help}>
                        <i className="fa fa-spinner fa-spin mr-1" /> Analizando archivo...
                      </div>
                    )}

                    {/* Validaciones en tiempo real */}
                    {csvError && (
                      <Alert type="danger" icon="alert-triangle">
                        {csvError}
                      </Alert>
                    )}

                    {!csvError && ids.length > 0 && (
                      <Alert type="success" icon="check">
                        {ids.length.toLocaleString()} movimiento(s) detectado(s)
                      </Alert>
                    )}

                    {duplicados > 0 && (
                      <Alert type="warning" icon="copy">
                        {duplicados} ID(s) repetido(s) detectado(s) — se contarán una sola vez.
                      </Alert>
                    )}

                    {invalidas.length > 0 && (
                      <Alert type="warning" icon="alert-circle">
                        {invalidas.length} línea(s) inválida(s) (se ignorarán):{" "}
                        {invalidas
                          .slice(0, 5)
                          .map((x) => `L${x.linea}: "${x.valor}"`)
                          .join(", ")}
                        {invalidas.length > 5 ? "…" : ""}
                      </Alert>
                    )}

                    {/* Preview: confirmación de IDs de movimiento */}
                    {ids.length > 0 && (
                      <div className={styles.previewWrap}>
                        <div className={styles.previewHeader}>
                          <span>
                            Vista previa · mostrando {previewInicioIdx + 1}–
                            {Math.min(previewInicioIdx + previewPageSize, ids.length)} de{" "}
                            {ids.length.toLocaleString()}
                          </span>
                          <span className={styles.pageSizeWrap}>
                            <label htmlFor="pageSize" className={styles.pageSizeLabel}>
                              Filas:
                            </label>
                            <select
                              id="pageSize"
                              className={styles.pageSizeSelect}
                              value={previewPageSize}
                              onChange={(e) => {
                                setPreviewPageSize(Number(e.target.value));
                                setPreviewPage(1);
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                          </span>
                        </div>
                        <div className={styles.previewScroll}>
                          <table className={styles.previewTable}>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>ID de movimiento</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.map((id, i) => (
                                <tr key={id}>
                                  <td>{previewInicioIdx + i + 1}</td>
                                  <td>{id}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {totalPreviewPages > 1 && (
                          <div className={styles.pagination}>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() => setPreviewPage(1)}
                              disabled={previewPage === 1}
                              aria-label="Primera página"
                            >
                              <Icon name="chevrons-left" />
                            </button>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                              disabled={previewPage === 1}
                              aria-label="Página anterior"
                            >
                              <Icon name="chevron-left" />
                            </button>
                            <span className={styles.pageInfo}>
                              Página{" "}
                              <input
                                type="number"
                                className={styles.pageInput}
                                min={1}
                                max={totalPreviewPages}
                                value={previewPage}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  if (!val) return;
                                  const clamped = Math.min(
                                    totalPreviewPages,
                                    Math.max(1, val)
                                  );
                                  setPreviewPage(clamped);
                                }}
                                aria-label="Ir a página"
                              />{" "}
                              de {totalPreviewPages}
                            </span>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() =>
                                setPreviewPage((p) => Math.min(totalPreviewPages, p + 1))
                              }
                              disabled={previewPage === totalPreviewPages}
                              aria-label="Página siguiente"
                            >
                              <Icon name="chevron-right" />
                            </button>
                            <button
                              type="button"
                              className={styles.pageBtn}
                              onClick={() => setPreviewPage(totalPreviewPages)}
                              disabled={previewPage === totalPreviewPages}
                              aria-label="Última página"
                            >
                              <Icon name="chevrons-right" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.actions}>
                      <Button color="secondary" onClick={() => setStep(1)}>
                        <Icon name="arrow-left" className="mr-1" /> Atrás
                      </Button>
                      <Button
                        color="primary"
                        disabled={!puedeAvanzarPaso2}
                        onClick={() => setStep(3)}
                      >
                        Revisar <Icon name="arrow-right" className="ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* =============================== PASO 3 =============================== */}
                {step === 3 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>Paso 3 · Revisión y confirmación</h4>

                    <div className={styles.summary}>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>GPS a colocar</span>
                        <span className={styles.summaryValue}>
                          <strong>{gps ? gps.label : "—"}</strong>
                        </span>
                      </div>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Movimientos</span>
                        <span className={styles.summaryValue}>
                          <strong>{ids.length.toLocaleString()}</strong>
                        </span>
                      </div>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Tipo de colocación</span>
                        <span className={styles.summaryValue}>1</span>
                      </div>
                      <div className={styles.summaryRow}>
                        <span className={styles.summaryLabel}>Incidencia</span>
                        <span className={styles.summaryValue}>
                          {conIncidencia ? <strong>{incidencia.label}</strong> : "Sin incidencia"}
                        </span>
                      </div>
                      {conIncidencia && comentarioIncidencia.trim() && (
                        <div className={styles.summaryRow}>
                          <span className={styles.summaryLabel}>Comentario</span>
                          <span className={styles.summaryValue + " " + styles.summaryText}>
                            {comentarioIncidencia.trim()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className={styles.infoBanner} role="note">
                      <Icon name="info" className={styles.infoIcon} />
                      <div>
                        <strong>Antes de confirmar</strong>
                        <ul className={styles.infoList}>
                          <li>
                            Los movimientos que reciban el GPS pasarán a estatus <strong>EN RUTA</strong>{" "}
                            (o PENDIENTE si son movimientos locales).
                          </li>
                          <li>
                            Se omitirán los movimientos que <strong>ya tengan GPS</strong> y los que
                            estén <strong>terminados o eliminados</strong>; al final verás el detalle.
                          </li>
                          {conIncidencia && (
                            <li>
                              La incidencia <strong>{incidencia.label}</strong> se registrará solo en
                              los movimientos con GPS asignado y donde esté habilitada para su
                              plataforma. No se envían notificaciones.
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>

                    <label className={styles.confirmCheck} htmlFor="confirmCheck">
                      <input
                        id="confirmCheck"
                        type="checkbox"
                        checked={confirmado}
                        onChange={(e) => setConfirmado(e.target.checked)}
                      />
                      <span>He revisado los datos y confirmo la asignación.</span>
                    </label>

                    {loading && (
                      <div className={styles.progressWrap} aria-live="polite">
                        <div className={styles.progressInfo}>
                          <span>
                            Asignando… {progreso.procesados.toLocaleString()} de{" "}
                            {progreso.total.toLocaleString()} movimiento(s)
                          </span>
                          <span>{porcentaje}%</span>
                        </div>
                        <div
                          className={styles.progressBar}
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={porcentaje}
                        >
                          <div
                            className={styles.progressFill}
                            style={{ width: porcentaje + "%" }}
                          />
                        </div>
                      </div>
                    )}

                    <div className={styles.actions}>
                      <Button color="secondary" disabled={loading} onClick={() => setStep(2)}>
                        <Icon name="arrow-left" className="mr-1" /> Atrás
                      </Button>
                      <Button color="secondary" disabled={loading} onClick={reiniciar}>
                        Cancelar
                      </Button>
                      <Button
                        color="primary"
                        disabled={!puedeConfirmar}
                        onClick={ejecutarAsignacion}
                      >
                        {loading ? (
                          <span>
                            <i className="fa fa-spinner fa-spin mr-1" /> Asignando…
                          </span>
                        ) : (
                          <span>
                            <Icon name="check" className="mr-1" /> Confirmar Asignación
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* =============================== PASO 4 =============================== */}
                {step === 4 && (
                  <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                      Paso 4 · Resultados · {gps ? gps.label : ""}
                    </h4>

                    <div className={styles.statsGrid}>
                      <div className={styles.statCard + " " + styles.statTotal}>
                        <span className={styles.statValue}>{resultados.length.toLocaleString()}</span>
                        <span className={styles.statLabel}>Procesados</span>
                      </div>
                      <div className={styles.statCard + " " + styles.statOk}>
                        <span className={styles.statValue}>{grupos.asignados.length.toLocaleString()}</span>
                        <span className={styles.statLabel}>Asignados</span>
                      </div>
                      <div className={styles.statCard + " " + styles.statWarn}>
                        <span className={styles.statValue}>
                          {(grupos.conGps.length + grupos.noColocables.length).toLocaleString()}
                        </span>
                        <span className={styles.statLabel}>Omitidos</span>
                      </div>
                      <div className={styles.statCard + " " + styles.statError}>
                        <span className={styles.statValue}>{grupos.errores.length.toLocaleString()}</span>
                        <span className={styles.statLabel}>Errores</span>
                      </div>
                    </div>

                    {conIncidencia && (
                      <Alert
                        type={grupos.incidenciasNoRegistradas.length > 0 ? "warning" : "success"}
                        icon="alert-circle"
                      >
                        Incidencia <strong>{incidencia.label}</strong>: registrada en{" "}
                        {grupos.incidenciasRegistradas.length.toLocaleString()} de{" "}
                        {grupos.asignados.length.toLocaleString()} movimiento(s) con GPS asignado.
                      </Alert>
                    )}

                    <TablaResultados
                      titulo="Incidencia no registrada"
                      icono="alert-circle"
                      variante="resultWarn"
                      filas={grupos.incidenciasNoRegistradas}
                      columnaDetalle="Motivo"
                      detalle={(r) => r.incidenciaMensaje}
                    />
                    <TablaResultados
                      titulo="Ya tienen GPS asignado"
                      icono="map-pin"
                      variante="resultWarn"
                      filas={grupos.conGps}
                      columnaDetalle="GPS actual"
                      detalle={(r) => r.gpsActual || "—"}
                    />
                    <TablaResultados
                      titulo="No se puede colocar (terminado o eliminado)"
                      icono="slash"
                      variante="resultWarn"
                      filas={grupos.noColocables}
                      columnaDetalle="Estatus del movimiento"
                      detalle={(r) => (r.motivo === "TERMINADO" ? "Terminado" : "Eliminado")}
                    />
                    <TablaResultados
                      titulo="Errores"
                      icono="alert-triangle"
                      variante="resultError"
                      filas={grupos.errores}
                      columnaDetalle="Detalle"
                      detalle={(r) => r.mensaje}
                    />
                    <TablaResultados
                      titulo="Asignados correctamente"
                      icono="check-circle"
                      variante="resultOk"
                      filas={grupos.asignados}
                      columnaDetalle="Detalle"
                      detalle={(r) =>
                        r.incidenciaMensaje ? r.mensaje + " · " + r.incidenciaMensaje : r.mensaje
                      }
                    />

                    <div className={styles.actions}>
                      <Button color="secondary" onClick={descargarResultados}>
                        <Icon name="download" className="mr-1" /> Descargar resultados
                      </Button>
                      <Button color="primary" onClick={reiniciar}>
                        <Icon name="refresh-cw" className="mr-1" /> Nueva asignación
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Grid.Col>
        </Grid.Row>
      </div>
      {loading && (
        <LoadingScreen
          loading={true}
          mensaje={
            "Asignando GPS... " +
            progreso.procesados.toLocaleString() +
            " / " +
            progreso.total.toLocaleString() +
            " (" +
            porcentaje +
            "%)"
          }
        />
      )}
    </SiteWrapper>
  );
};

export default AsignacionMasivaGps;
